import { kpiPreset } from "./dashboard-widgets";
import { MODEL_VERSION } from "./demo-data";
import { simpleQuery } from "./guided-query";
import type {
  QueryDefinition,
  VisualizationType,
  FieldFormat,
  DashboardDefinition,
  DashboardWidget,
} from "./types";
export const STORAGE_KEY = "luuta.analytics.malloy.v1.north-coast";
const author = { id: "luuta-analytics", name: "Luuta Analytics" };
function report(
  id: string,
  name: string,
  description: string,
  source: string,
  measures: string[],
  groups: string[],
  type: VisualizationType,
  format: FieldFormat = "number",
): QueryDefinition {
  const query = simpleQuery(source, measures, groups);
  if (
    type === "line" &&
    query.definition.kind === "arrow" &&
    query.definition.view.kind === "segment"
  )
    query.definition.view.operations.push({
      kind: "order_by",
      field_reference: { name: groups[0] },
      direction: "asc",
    });
  return {
    id,
    name,
    description,
    modelVersion: MODEL_VERSION,
    mode: "guided",
    query,
    tags: [source.replaceAll("_", " "), type],
    author,
    builtin: true,
    visualization: { type, x: groups[0] ?? "", y: measures, format },
  };
}
export const BUILTIN_REPORTS: QueryDefinition[] = [
  report(
    "task-summary",
    "Cleaning completion",
    "Recorded completion evidence and task deadlines.",
    "cleaning_tasks",
    [
      "task_count",
      "completed_task_count",
      "on_time_completion_pct",
      "invalid_completions",
    ],
    [],
    "table",
  ),
  report(
    "unit-readiness",
    "Units currently clean",
    "Current active-unit snapshot. Historical period filters do not apply.",
    "current_units",
    ["currently_clean_pct"],
    [],
    "kpi",
    "percent",
  ),
  report(
    "completion-trend",
    "Completion by deadline",
    "Non-cancelled due tasks, including overdue incomplete work.",
    "cleaning_tasks",
    ["on_time_completion_pct"],
    ["work_date"],
    "line",
    "percent",
  ),
  report(
    "completion-by-property",
    "Completion by property",
    "Task completion against each recorded deadline.",
    "cleaning_tasks",
    ["completed_task_count", "due_task_count"],
    ["property_name"],
    "bar",
  ),
  report(
    "unit-states",
    "Current unit states",
    "Active units at the fixed snapshot, regardless of selected historical period.",
    "current_units",
    ["unit_count"],
    ["cleaning_state"],
    "bar",
  ),
  report(
    "performance-summary",
    "Recorded session effort",
    "Closed per-person session spans, including breaks.",
    "cleaner_sessions",
    ["recorded_person_hours"],
    [],
    "kpi",
    "hours",
  ),
  report(
    "effort-by-type",
    "Effort by unit type",
    "Recorded person-hours by current unit classification.",
    "cleaner_sessions",
    ["recorded_person_hours"],
    ["unit_type"],
    "bar",
    "hours",
  ),
  report(
    "effort-by-employee",
    "Employee session effort",
    "Session time belongs to each employee; task quality remains task-level.",
    "cleaner_sessions",
    ["recorded_person_hours"],
    ["employee_name"],
    "bar",
    "hours",
  ),
  report(
    "quality-trend",
    "First-inspection quality",
    "Earliest linked result; a later pass preserves the first failure.",
    "cleaning_tasks",
    ["first_inspection_quality_pct"],
    ["work_date"],
    "line",
    "percent",
  ),
  report(
    "inspection-coverage",
    "Inspection coverage",
    "Non-cancelled tasks with submitted linked inspections.",
    "cleaning_tasks",
    ["inspection_coverage_pct"],
    ["property_name"],
    "bar",
    "percent",
  ),
  report(
    "break-time",
    "Actual break time",
    "Observed break intervals, not duration entitlements.",
    "cleaner_sessions",
    ["actual_coffee_minutes", "actual_meal_minutes"],
    ["property_name"],
    "bar",
    "minutes",
  ),
  report(
    "supply-delays",
    "Supply-order context",
    "Submitted-order age is elapsed order age, not cleaner waiting labor.",
    "supply_orders",
    ["submitted_order_count", "oldest_submitted_order_minutes"],
    ["property_name"],
    "table",
  ),
  report(
    "data-quality",
    "Timestamp diagnostics",
    "Open sessions and missing/invalid timestamp evidence.",
    "cleaning_tasks",
    ["open_sessions", "invalid_sessions", "invalid_completions"],
    ["property_name"],
    "table",
  ),
  {
    id: "nested-task-report",
    name: "Task status detail",
    description:
      "A nested source query report. Expand each property to inspect task states.",
    modelVersion: MODEL_VERSION,
    mode: "malloy",
    malloy:
      "run: cleaning_tasks -> { group_by: property_name aggregate: task_count nest: statuses is { group_by: status aggregate: task_count } }",
    tags: ["cleaning tasks", "nested"],
    author,
    builtin: true,
    visualization: {
      type: "table",
      x: "property_name",
      y: ["task_count"],
      format: "number",
    },
  },
];
// TEHO templates use the same saved-report and widget contracts as every dashboard.
const tehoKpis = {
  "cleaner-teho": [
    ["contribution", "Cleaner TEHO · contribution", "currency"],
    ["revenue", "Cleaning revenue", "currency"],
    ["wages", "Full-shift wages", "currency"],
    ["contribution_margin", "Contribution margin", "ratio"],
    ["revenue_per_paid_hour", "Revenue per paid hour", "currency"],
    ["paid_hours", "Paid hours", "hours"],
  ],
  "driver-teho": [
    ["teho_score", "Driver TEHO · illustrative / 100", "number"],
    ["completed_deliveries", "Completed deliveries", "number"],
    ["physical_trips", "Physical trips", "number"],
    ["sla_compliance", "SLA compliance", "ratio"],
    ["urgent_handling", "Urgent on-time handling", "ratio"],
    ["route_efficiency", "Route efficiency", "ratio"],
    ["repeat_trips", "Repeat trips", "number"],
    ["dog_help_share", "Dog/help share of completed deliveries", "ratio"],
    ["cleaner_waiting", "Cleaner waiting (person-minutes)", "minutes"],
    ["paid_hours", "Paid hours", "hours"],
  ],
} as const;
const tehoPanels: Record<
  keyof typeof tehoKpis,
  [string, string, VisualizationType, string, string[], FieldFormat][]
> = {
  "cleaner-teho": [
    [
      "daily_finances",
      "Daily revenue, wages and contribution",
      "line",
      "work_date",
      ["revenue", "wages", "contribution"],
      "currency",
    ],
    [
      "cleaner_comparison",
      "Contribution by cleaner",
      "bar",
      "employee_name",
      ["contribution"],
      "currency",
    ],
    [
      "payroll_evidence",
      "Payroll coverage · lunch excluded, coffee included",
      "table",
      "",
      [],
      "number",
    ],
    [
      "shift_detail",
      "Shift and task detail · first 100 shifts; up to 100 tasks each",
      "table",
      "",
      [],
      "number",
    ],
  ],
  "driver-teho": [
    [
      "daily_score",
      "Daily Driver TEHO",
      "line",
      "work_date",
      ["teho_score"],
      "number",
    ],
    [
      "deliveries_by_driver",
      "Completed deliveries by driver",
      "bar",
      "employee_name",
      ["completed_deliveries"],
      "number",
    ],
    [
      "components",
      "Score components · 2 credits/hour target; 10 waiting person-minutes/request budget",
      "table",
      "",
      [],
      "number",
    ],
    [
      "weights",
      "Effective weights · N/A redistributes",
      "table",
      "",
      [],
      "number",
    ],
    ["drivers", "Driver comparison", "table", "", [], "number"],
    [
      "deliveries",
      "Delivery and trip detail · first 100 requests; up to 100 trips each",
      "table",
      "",
      [],
      "number",
    ],
  ],
};
for (const id of Object.keys(tehoKpis) as (keyof typeof tehoKpis)[]) {
  const source = id.replaceAll("-", "_");
  const metadata = {
    modelVersion: MODEL_VERSION,
    mode: "malloy" as const,
    author,
    builtin: true,
    tags: ["TEHO", id],
  };
  tehoKpis[id].forEach(([field, name, format], index) => {
    BUILTIN_REPORTS.push({
      ...metadata,
      id: `${id}-${field}`,
      name,
      description: "Selected properties and reporting period",
      malloy: `run: ${source} -> summary`,
      visualization: {
        type: "kpi",
        x: "",
        y: [field],
        format,
        fieldLabels: { [field]: name },
        kpi: kpiPreset(index === 0 ? "highlighted" : "neutral"),
        showHeader: false,
        showDescription: false,
        showFooter: false,
      },
    });
  });
  for (const [view, name, type, x, y, format] of tehoPanels[id]) {
    BUILTIN_REPORTS.push({
      ...metadata,
      id: `${id}-${view}`,
      name,
      description: "Selected properties and reporting period",
      malloy: `run: ${source} -> ${view}`,
      visualization: { type, x, y, format, showFooter: false },
    });
  }
}
function tehoWidgets(
  id: keyof typeof tehoKpis,
  width: DashboardWidget["width"],
): DashboardWidget[] {
  return BUILTIN_REPORTS.filter((r) => r.tags.includes(id)).map((report) => ({
    id: `${id}-${report.id}`,
    reportId: report.id,
    width:
      report.visualization.type === "kpi"
        ? width
        : report.visualization.type === "table"
          ? "full"
          : "half",
  }));
}
export const BUILTIN_DASHBOARDS: DashboardDefinition[] = [
  {
    id: "cleaner-teho",
    name: "Cleaner TEHO",
    description:
      "Illustrative cleaning revenue minus full-shift direct wages, before overhead. Lunch excluded; coffee included.",
    widgets: tehoWidgets("cleaner-teho", "third"),
    builtin: true,
  },
  {
    id: "driver-teho",
    name: "Driver TEHO",
    description:
      "Illustrative driver score with deliveries, repeat trips, SLA, dog/help work, urgent handling, routes and cleaner waiting.",
    widgets: tehoWidgets("driver-teho", "fifth"),
    builtin: true,
  },
  {
    id: "operations",
    name: "Operations overview",
    description:
      "Task completion, current unit state, inspection quality and supply context.",
    widgets: [
      "unit-readiness",
      "completion-by-property",
      "completion-trend",
      "unit-states",
      "inspection-coverage",
      "supply-delays",
    ].map((reportId, index) => ({
      id: `${reportId}-${index}`,
      reportId,
      width: "half" as const,
    })),
    builtin: true,
  },
  {
    id: "productivity",
    name: "Effort & quality",
    description:
      "Recorded employee-session effort with task-level quality and timestamp diagnostics.",
    widgets: [
      "effort-by-employee",
      "effort-by-type",
      "quality-trend",
      "break-time",
      "data-quality",
    ].map((reportId, index) => ({
      id: `${reportId}-${index}`,
      reportId,
      width: "half" as const,
    })),
    builtin: true,
  },
];
